export const downloadFileFromBlob = (filename: string, data: Blob) => {
  const link = document.createElement('a');
  link.download = filename;
  link.target = '_blank';
  link.href = URL.createObjectURL(data);
  link.click();
};

export const renameFile = (file: File, name: string) => {
  return new File([file], `${name}.jpg`, { type: file.type });
};

export const MIN_IMAGE_DIMENSION_PX = 128;

const isImageFile = (file: File): boolean => file.type.includes('image');

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    image.src = objectUrl;
  });
};

export const isImageTooSmall = async (file: File): Promise<boolean> => {
  if (!isImageFile(file)) return false;

  try {
    const { width, height } = await getImageDimensions(file);
    return width <= MIN_IMAGE_DIMENSION_PX || height <= MIN_IMAGE_DIMENSION_PX;
  } catch {
    return false;
  }
};

/**
 * IndexedDB-safe representation of a picked file.
 *
 * A File coming out of a file input is backed by an OS temp file, and structured
 * cloning it into IndexedDB stores a reference to that backing store rather than a
 * copy of the bytes. iOS reaps those temp files when a standalone PWA is
 * backgrounded - which is exactly what opening the photo picker or the camera does
 * - so the File read back out has intact name/size/type but no content, and the
 * upload posts an empty part. Persisting the ArrayBuffer stores a real copy that
 * nothing can reap.
 */
export interface StoredFile {
  name: string;
  type: string;
  lastModified: number;
  buffer: ArrayBuffer;
}

export const toStoredFile = async (file: File): Promise<StoredFile> => ({
  name: file.name,
  type: file.type,
  lastModified: file.lastModified,
  buffer: await file.arrayBuffer()
});

export const fromStoredFile = (stored: StoredFile): File =>
  new File([stored.buffer], stored.name, { type: stored.type, lastModified: stored.lastModified });

const isStoredFile = (value: unknown): value is StoredFile =>
  !!value && typeof value === 'object' && (value as StoredFile).buffer instanceof ArrayBuffer;

/**
 * Drafts started before files were persisted as buffers still hold raw File objects,
 * so both shapes are read back.
 */
export const toFiles = (stored: unknown): File[] => {
  if (!Array.isArray(stored)) return [];
  return stored.map((entry) => (isStoredFile(entry) ? fromStoredFile(entry) : (entry as File)));
};
